import React, { useState, useEffect, useCallback } from 'react';
import {
    FileText, Upload, Download, Trash2, Search, Filter,
    File as LucideFile, FileSpreadsheet, FileImage, FolderOpen, X,
    FileEdit, Presentation, Settings, LayoutGrid, List, Eye, Calendar
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api, { API_BASE_URL } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { AddButton } from '../components/ui/AddButton';
import { ConfirmDialog } from '../components/ui/confirm-dialog';


interface AppDocument {
    id: number;
    title: string;
    description: string | null;
    category: string;
    brand: string;
    brands?: string[] | null;
    filename: string;
    file_size: number;
    mime_type: string;
    uploaded_by: number;
    admin_id: number;
    is_public: boolean;
    created_at: string;
    updated_at: string;
    download_count: number;
    uploader_name: string | null;
    download_url: string | null;
    visible_to_client?: boolean;
    visible_to_apporteur?: boolean;
    visible_to_transporteur?: boolean;
    visible_to_fournisseur?: boolean;
}

interface Category {
    value: string;
    label: string;
}

const resolveCategoryLabel = (categories: Category[], category: string): string => {
    const normalized = category.trim().toLowerCase();
    const match = categories.find((item) => {
        const valueNorm = item.value.trim().toLowerCase();
        const labelNorm = item.label.trim().toLowerCase();
        return valueNorm === normalized || labelNorm === normalized;
    });
    return match?.label || category;
};

interface Brand {
    value: string;
    label: string;
}

const brandColors: Record<string, { bg: string; text: string; label: string }> = {
    nova_flora: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400', label: 'NOVA FLORA' },
    douce_france: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', label: 'DOUCE FRANCE' },
};

const getFileIcon = (mimeType: string, filename: string): React.ElementType => {
    const ext = filename.split('.').pop()?.toLowerCase() || '';

    if (mimeType.includes('pdf') || ext === 'pdf') return FileText;

    // Excel / Spreadsheets
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || ['xls', 'xlsx', 'csv', 'ods'].includes(ext)) {
        return FileSpreadsheet;
    }

    // PowerPoint / Presentations
    if (mimeType.includes('presentation') || mimeType.includes('powerpoint') || ['ppt', 'pptx', 'odp'].includes(ext)) {
        return Presentation;
    }

    // Word / Documents
    if (mimeType.includes('word') || mimeType.includes('officedocument.wordprocessingml') || ['doc', 'docx', 'odt', 'txt'].includes(ext)) {
        return FileEdit;
    }

    if (mimeType.includes('image')) return FileImage;

    return LucideFile;
};

export default function Documentation() {
    const [documents, setDocuments] = useState<AppDocument[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [brands, setBrands] = useState<Brand[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string>('');
    const [selectedBrand, setSelectedBrand] = useState<string>('');
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [editingDocument, setEditingDocument] = useState<AppDocument | null>(null);
    const [viewingDocument, setViewingDocument] = useState<AppDocument | null>(null);
    const [documentUrl, setDocumentUrl] = useState<string | null>(null);
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
    const [sortConfig, setSortConfig] = useState<{ key: 'title' | 'category'; direction: 'asc' | 'desc' }>({ key: 'title', direction: 'asc' });

    const sortedDocuments = [...documents].sort((a, b) => {
        if (sortConfig.key === 'title') {
            return sortConfig.direction === 'asc'
                ? a.title.localeCompare(b.title, 'fr', { sensitivity: 'base' })
                : b.title.localeCompare(a.title, 'fr', { sensitivity: 'base' });
        }
        if (sortConfig.key === 'category') {
            const labelA = resolveCategoryLabel(categories, a.category);
            const labelB = resolveCategoryLabel(categories, b.category);
            return sortConfig.direction === 'asc'
                ? labelA.localeCompare(labelB, 'fr', { sensitivity: 'base' })
                : labelB.localeCompare(labelA, 'fr', { sensitivity: 'base' });
        }
        return 0;
    });

    const handleSort = (key: 'title' | 'category') => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const sortedCategories: Category[] = [...categories].sort((first: Category, second: Category) => (
        first.label.localeCompare(second.label, 'fr', { sensitivity: 'base' })
    ));

    const [loadingPreview, setLoadingPreview] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; document: AppDocument | null }>({ open: false, document: null });
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const { user } = useAuthStore();
    const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
    // const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'apporteur';
    // Debug logging - remove in production
    console.log('User role:', user?.role, 'Is admin:', isAdmin);

    const loadDocuments = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (selectedCategory) params.append('category', selectedCategory);
            if (selectedBrand) params.append('brand', selectedBrand);
            if (searchQuery) params.append('search', searchQuery);

            const response = await api.get(`/documents?${params.toString()}`);

            // Remove download_url for non-admin users
            const documentsData = response.data.map((doc: AppDocument) => ({
                ...doc,
                download_url: isAdmin ? doc.download_url : null
            }));

            setDocuments(documentsData);
        } catch (error: any) {
            console.error('Error loading documents:', error);
            toast.error('Erreur lors du chargement des documents');
        } finally {
            setLoading(false);
        }
    }, [selectedCategory, selectedBrand, searchQuery, isAdmin]);

    const loadCategories = async () => {
        try {
            const response = await api.get('/documents/categories');
            setCategories(response.data);
        } catch (error) {
            console.error('Error loading categories:', error);
        }
    };

    const loadBrands = async () => {
        try {
            const response = await api.get('/documents/brands');
            setBrands(response.data);
        } catch (error) {
            console.error('Error loading brands:', error);
        }
    };

    useEffect(() => {
        loadCategories();
        loadBrands();
    }, []);

    useEffect(() => {
        loadDocuments();
    }, [loadDocuments]);

    const handleView = (doc: AppDocument) => {
        const viewingDoc = { ...doc, download_url: isAdmin ? doc.download_url : null };
        setViewingDocument(viewingDoc);
        setDocumentUrl(null);
        setLoadingPreview(true);

        const fileExt = doc.filename.split('.').pop()?.toLowerCase() || '';
        const officeExtensions = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp'];
        const isOfficeFile = officeExtensions.includes(fileExt);
        const isPdf = fileExt === 'pdf' || doc.mime_type === 'application/pdf';
        const isImage = doc.mime_type.includes('image');

        const token = useAuthStore.getState().token;
        if ((isOfficeFile || isPdf || isImage) && token) {
            setDocumentUrl(`${API_BASE_URL}/documents/${doc.id}/preview-pdf?token=${encodeURIComponent(token)}`);
        }
        setLoadingPreview(false);
    };

    const closeViewer = () => {
        setViewingDocument(null);
        setDocumentUrl(null);
    };

    const handleDownload = async (doc: AppDocument) => {
        try {
            const token = useAuthStore.getState().token;
            if (!token) {
                toast.error('Non authentifié');
                return;
            }

            // Use direct URL with token for download
            const downloadUrl = `${API_BASE_URL}/documents/${doc.id}/download?token=${encodeURIComponent(token)}`;

            // Create temporary link to trigger download
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', doc.filename);
            document.body.appendChild(link);
            link.click();
            link.remove();

            setDocuments(prev => prev.map(d =>
                d.id === doc.id ? { ...d, download_count: d.download_count + 1 } : d
            ));
        } catch (error) {
            console.error('Error downloading document:', error);
            toast.error('Erreur lors du téléchargement');
        }
    };

    const handleDelete = (doc: AppDocument) => {
        setDeleteDialog({ open: true, document: doc });
    };

    const handleConfirmDelete = async () => {
        if (!deleteDialog.document) return;

        setIsDeleting(true);
        try {
            await api.delete(`/documents/${deleteDialog.document.id}`);
            toast.success('Document supprimé');
            setDeleteDialog({ open: false, document: null });
            loadDocuments();
        } catch (error) {
            console.error('Error deleting document:', error);
            toast.error('Erreur lors de la suppression');
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className="p-3 sm:p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 sm:mb-6 gap-3 sm:gap-4">
                <div>
                    <h1 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-slate-100">
                        Documentation
                    </h1>
                    <p className="text-sm sm:text-base text-slate-500 dark:text-slate-400 mt-1">
                        Documents de formation, certificats et conformité
                    </p>
                </div>

                {isAdmin && (
                    <AddButton
                        onClick={() => setShowUploadModal(true)}
                        label="Ajouter un document"
                    />
                )}
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-3 sm:p-4 mb-4 sm:mb-6">
                <div className="flex flex-col gap-3 sm:gap-4">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Rechercher un document..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-9 sm:pl-10 pr-4 py-2 text-sm sm:text-base border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                        />
                    </div>

                    <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                        <Filter className="h-4 w-4 sm:h-5 sm:w-5 text-slate-400 hidden sm:block" />
                        {isAdmin && (
                            <select
                                value={selectedBrand}
                                onChange={(e) => setSelectedBrand(e.target.value)}
                                className="flex-1 min-w-0 px-2 sm:px-4 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                            >
                                <option value="">Toutes les marques</option>
                                {brands.map(brand => (
                                    <option key={brand.value} value={brand.value}>{brand.label}</option>
                                ))}
                            </select>
                        )}
                        <select
                            value={sortConfig.key}
                            onChange={(e) => handleSort(e.target.value as 'title' | 'category')}
                            className="flex-1 min-w-0 px-2 sm:px-4 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                        >
                            <option value="title">Trier par Titre</option>
                            <option value="category">Trier par Catégorie</option>
                        </select>

                        <select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            className="flex-1 min-w-0 px-2 sm:px-4 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                        >
                            <option value="">Toutes les catégories</option>
                            {sortedCategories.map((cat) => (
                                <option key={cat.value} value={cat.value}>{cat.label}</option>
                            ))}
                        </select>

                        {/* Toggle Vue Grille / Liste */}
                        <div className="flex items-center bg-slate-100 dark:bg-slate-700 rounded-lg p-1">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`p-2 rounded-md transition-all ${viewMode === 'grid'
                                    ? 'bg-white dark:bg-slate-600 text-purple-600 dark:text-purple-400 shadow-sm'
                                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                                    }`}
                                title="Vue grille"
                            >
                                <LayoutGrid className="h-4 w-4" />
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-2 rounded-md transition-all ${viewMode === 'list'
                                    ? 'bg-white dark:bg-slate-600 text-purple-600 dark:text-purple-400 shadow-sm'
                                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                                    }`}
                                title="Vue liste"
                            >
                                <List className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Documents Grid */}
            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
            ) : documents.length === 0 ? (
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-12 text-center">
                    <FolderOpen className="h-16 w-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Aucun document
                    </h3>
                    <p className="text-slate-500 dark:text-slate-400">
                        {searchQuery || selectedCategory
                            ? 'Aucun document ne correspond à vos critères de recherche'
                            : 'Aucun document n\'a encore été ajouté'}
                    </p>
                </div>
            ) : viewMode === 'grid' ? (
                /* ========== VUE GRILLE ========== */
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
                    {sortedDocuments.map(doc => {
                        const DocumentFileIcon = getFileIcon(doc.mime_type, doc.filename);

                        return (
                            <div
                                key={doc.id}
                                onClick={() => handleView(doc)}
                                className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 hover:shadow-lg hover:border-purple-300 dark:hover:border-purple-600 transition-all duration-200 cursor-pointer group flex flex-col h-full overflow-hidden"
                            >
                                {/* Header coloré avec icône */}
                                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 px-3 sm:px-5 py-3 sm:py-4 border-b border-slate-100 dark:border-slate-700">
                                    <div className="flex items-start gap-4">
                                        <div className="p-3 bg-white dark:bg-slate-700 rounded-xl shadow-sm group-hover:shadow-md transition-shadow">
                                            <DocumentFileIcon className="h-7 w-7 text-purple-600 dark:text-purple-400" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-semibold text-slate-800 dark:text-slate-100 text-base leading-tight line-clamp-2" title={doc.title}>
                                                {doc.title}
                                            </h3>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-3 sm:p-5 flex flex-col flex-1">
                                    {/* Description */}
                                    {doc.description && (
                                        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 line-clamp-2">
                                            {doc.description}
                                        </p>
                                    )}

                                    {/* Tags */}
                                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                                        {doc.brand && brandColors[doc.brand] && (
                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold ${brandColors[doc.brand].bg} ${brandColors[doc.brand].text}`}>
                                                {brandColors[doc.brand].label}
                                            </span>
                                        )}
                                        {isAdmin && (
                                            <>
                                                {doc.is_public && (
                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                                        Public
                                                    </span>
                                                )}
                                                {!doc.is_public && (
                                                    <>
                                                        {doc.visible_to_client && (
                                                            <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                                                                Client
                                                            </span>
                                                        )}
                                                        {doc.visible_to_apporteur && (
                                                            <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                                                                Apporteur
                                                            </span>
                                                        )}
                                                        {doc.visible_to_transporteur && (
                                                            <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                                                                Transporteur
                                                            </span>
                                                        )}
                                                        {doc.visible_to_fournisseur && (
                                                            <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                                                                Fournisseur
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </div>

                                    {/* Métadonnées */}
                                    <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400 mb-4 flex-wrap">
                                        <span className="flex items-center bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded-md">
                                            {resolveCategoryLabel(categories, doc.category)}
                                        </span>
                                    </div>

                                    {/* Date */}
                                    <div className="flex items-center gap-1.5 text-xs text-slate-400 dark:text-slate-500 mb-4">
                                        <Calendar className="h-3.5 w-3.5" />
                                        {isAdmin
                                            ? `Ajouté par ${doc.uploader_name || 'Admin'} le ${new Date(doc.created_at).toLocaleDateString('fr-FR')}`
                                            : `Ajouté le ${new Date(doc.created_at).toLocaleDateString('fr-FR')}`
                                        }
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center gap-2 mt-auto pt-3 border-t border-slate-100 dark:border-slate-700">
                                        {(isAdmin && user?.role) ? (
                                            <>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDownload(doc);
                                                    }}
                                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all text-sm font-medium shadow-sm"
                                                >
                                                    <Download className="h-4 w-4" />
                                                    Télécharger
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setEditingDocument(doc);
                                                    }}
                                                    className="p-2.5 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                                    title="Paramètres"
                                                >
                                                    <Settings className="h-4 w-4" />
                                                </button>
                                            </>
                                        ) : (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleView(doc);
                                                }}
                                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all text-sm font-medium shadow-sm"
                                            >
                                                <Eye className="h-4 w-4" />
                                                Visualiser
                                            </button>
                                        )}

                                        {isAdmin && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDelete(doc);
                                                }}
                                                className="p-2.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                title="Supprimer"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                /* ========== VUE LISTE ========== */
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
                    {/* Header de la liste */}
                    <div className="hidden md:grid md:grid-cols-12 gap-4 px-6 py-3 bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-600 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                        <div
                            className="col-span-6 flex items-center gap-1 cursor-pointer hover:text-purple-600 transition-colors"
                            onClick={() => handleSort('title')}
                        >
                            Document
                            <div className="flex flex-col ml-1">
                                <span className={`leading-[0] ${sortConfig.key === 'title' && sortConfig.direction === 'asc' ? 'text-purple-500' : 'text-slate-300 dark:text-slate-600'}`}>▲</span>
                                <span className={`leading-[0] ${sortConfig.key === 'title' && sortConfig.direction === 'desc' ? 'text-purple-500' : 'text-slate-300 dark:text-slate-600'}`}>▼</span>
                            </div>
                        </div>
                        <div
                            className="col-span-3 flex items-center gap-1 cursor-pointer hover:text-purple-600 transition-colors"
                            onClick={() => handleSort('category')}
                        >
                            Catégorie
                            <div className="flex flex-col ml-1">
                                <span className={`leading-[0] ${sortConfig.key === 'category' && sortConfig.direction === 'asc' ? 'text-purple-500' : 'text-slate-300 dark:text-slate-600'}`}>▲</span>
                                <span className={`leading-[0] ${sortConfig.key === 'category' && sortConfig.direction === 'desc' ? 'text-purple-500' : 'text-slate-300 dark:text-slate-600'}`}>▼</span>
                            </div>
                        </div>
                        <div className="col-span-3 text-right px-4">Date</div>
                    </div>

                    {/* Liste des documents */}
                    <div className="divide-y divide-slate-100 dark:divide-slate-700">
                        {sortedDocuments.map(doc => {
                            const DocumentFileIcon = getFileIcon(doc.mime_type, doc.filename);

                            return (
                                <div
                                    key={doc.id}
                                    onClick={() => handleView(doc)}
                                    className="grid grid-cols-1 md:grid-cols-12 gap-2 sm:gap-4 px-3 sm:px-6 py-3 sm:py-4 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors cursor-pointer group items-center"
                                >
                                    {/* Document info */}
                                    <div className="md:col-span-6 flex items-center gap-4">
                                        <div className="p-2.5 bg-purple-100 dark:bg-purple-900/30 rounded-lg group-hover:bg-purple-200 dark:group-hover:bg-purple-800/50 transition-colors flex-shrink-0">
                                            <DocumentFileIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <h3 className="font-medium text-slate-800 dark:text-slate-100 line-clamp-2" title={doc.title}>
                                                    {doc.title}
                                                </h3>
                                                {doc.brand && brandColors[doc.brand] && (
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${brandColors[doc.brand].bg} ${brandColors[doc.brand].text}`}>
                                                        {brandColors[doc.brand].label}
                                                    </span>
                                                )}
                                                {isAdmin && doc.is_public && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                                        Public
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Catégorie */}
                                    <div className="md:col-span-3 text-sm text-slate-600 dark:text-slate-400">
                                        <span className="truncate">{resolveCategoryLabel(categories, doc.category)}</span>
                                    </div>

                                    {/* Date et Actions */}
                                    <div className="md:col-span-3 flex items-center justify-end gap-6 text-sm">
                                        <div className="text-slate-500 dark:text-slate-400 flex flex-col items-end">
                                            <div className="flex items-center gap-1.5">
                                                <Calendar className="h-3.5 w-3.5 flex-shrink-0" />
                                                {new Date(doc.created_at).toLocaleDateString('fr-FR')}
                                            </div>
                                            {isAdmin && (
                                                <span className="text-[10px] text-slate-400 dark:text-slate-500">
                                                    par {doc.uploader_name || 'Admin'}
                                                </span>
                                            )}
                                        </div>

                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            {(isAdmin && user?.role) ? (
                                                <>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDownload(doc);
                                                        }}
                                                        className="p-2 text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded-lg transition-colors"
                                                        title="Télécharger"
                                                    >
                                                        <Download className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setEditingDocument(doc);
                                                        }}
                                                        className="p-2 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                                        title="Paramètres"
                                                    >
                                                        <Settings className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDelete(doc);
                                                        }}
                                                        className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                        title="Supprimer"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </>
                                            ) : (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleView(doc);
                                                    }}
                                                    className="p-2 text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded-lg transition-colors"
                                                    title="Visualiser"
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Modals */}
            {showUploadModal && (
                <UploadModal
                    categories={sortedCategories}
                    onClose={() => setShowUploadModal(false)}
                    onSuccess={() => {
                        setShowUploadModal(false);
                        loadDocuments();
                    }}
                />
            )}

            {editingDocument && (
                <EditModal
                    document={editingDocument}
                    categories={sortedCategories}
                    onClose={() => setEditingDocument(null)}
                    onSuccess={() => {
                        setEditingDocument(null);
                        loadDocuments();
                    }}
                />
            )}

            {viewingDocument && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-2 sm:p-4">
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-6xl h-[90vh] flex flex-col">
                        <div className="flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4 border-b border-slate-200 dark:border-slate-700 gap-2">
                            <div className="min-w-0 flex-1">
                                <h2 className="text-sm sm:text-lg font-semibold text-slate-800 dark:text-slate-100 truncate">
                                    {viewingDocument.title}
                                </h2>
                                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 truncate">
                                    {viewingDocument.filename}
                                </p>
                            </div>
                            <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                                {isAdmin && (
                                    <>
                                        <button
                                            onClick={() => handleDownload(viewingDocument)}
                                            className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                                        >
                                            <Download className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                            <span className="hidden sm:inline">Télécharger</span>
                                        </button>
                                        <button
                                            onClick={() => {
                                                closeViewer();
                                                setEditingDocument(viewingDocument);
                                            }}
                                            className="p-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                            title="Paramètres"
                                        >
                                            <Settings className="h-5 w-5" />
                                        </button>
                                    </>
                                )}
                                <button
                                    onClick={closeViewer}
                                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-2"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-hidden p-4">
                            {loadingPreview ? (
                                <div className="flex items-center justify-center h-full">
                                    <div className="text-center">
                                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                                        <p className="text-slate-600 dark:text-slate-400">Chargement du document...</p>
                                    </div>
                                </div>

                            ) : documentUrl ? (
                                    <iframe
                                        src={`${documentUrl}#view=Fit`}
                                        className="w-full h-full"
                                        style={{
                                            border: 'none',
                                            // Some mobile browsers respect zoom to force the document smaller
                                            zoom: '0.99',
                                            width: '100%',
                                        }}
                                    />
                            ) : (
                                <div className="flex items-center justify-center h-full">
                                    <div className="text-center p-8 bg-slate-50 dark:bg-slate-900/50 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700">
                                        <FileText className="h-16 w-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                                        <h3 className="text-lg font-medium text-slate-800 dark:text-slate-200 mb-2">
                                            Aperçu non disponible
                                        </h3>
                                        <p className="text-slate-600 dark:text-slate-400 mb-6 max-w-sm">
                                            Ce fichier ne peut pas être visualisé directement dans le navigateur.
                                        </p>
                                        {isAdmin && (
                                            <button
                                                onClick={() => handleDownload(viewingDocument)}
                                                className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors mx-auto font-medium shadow-sm shadow-purple-200 dark:shadow-none"
                                            >
                                                <Download className="h-5 w-5" />
                                                Télécharger pour ouvrir
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Dialog */}
            <ConfirmDialog
                open={deleteDialog.open}
                onOpenChange={(open) => setDeleteDialog({ open, document: open ? deleteDialog.document : null })}
                title="Supprimer le document"
                description={`Êtes-vous sûr de vouloir supprimer le document "${deleteDialog.document?.title}" ? Cette action est irréversible.`}
                confirmText="Supprimer"
                cancelText="Annuler"
                isLoading={isDeleting}
                onConfirm={handleConfirmDelete}
            />
        </div>
    );
}

interface UploadModalProps {
    categories: Category[];
    onClose: () => void;
    onSuccess: () => void;
}

function UploadModal({ categories, onClose, onSuccess }: UploadModalProps) {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState(categories[0]?.value || '');
    const [brand, setBrand] = useState('nova_flora');
    const [isPublic, setIsPublic] = useState(true);
    const [visibleToClient, setVisibleToClient] = useState(true);
    const [visibleToApporteur, setVisibleToApporteur] = useState(true);
    const [visibleToTransporteur, setVisibleToTransporteur] = useState(true);
    const [visibleToFournisseur, setVisibleToFournisseur] = useState(true);
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const sortedCategories: Category[] = [...categories].sort((first: Category, second: Category) => (
        first.label.localeCompare(second.label, 'fr', { sensitivity: 'base' })
    ));

    useEffect(() => {
        if (categories.length > 0 && !category) {
            setCategory(categories[0].value);
        }
    }, [categories, category]);

    const handleIsPublicChange = (checked: boolean) => {
        setIsPublic(checked);
        if (checked) {
            setVisibleToClient(true);
            setVisibleToApporteur(true);
            setVisibleToTransporteur(true);
            setVisibleToFournisseur(true);
        }
    };

    const handleRoleVisibilityChange = (role: 'client' | 'apporteur' | 'transporteur' | 'fournisseur', checked: boolean) => {
        switch (role) {
            case 'client':
                setVisibleToClient(checked);
                break;
            case 'apporteur':
                setVisibleToApporteur(checked);
                break;
            case 'transporteur':
                setVisibleToTransporteur(checked);
                break;
            case 'fournisseur':
                setVisibleToFournisseur(checked);
                break;
        }

        if (!checked) {
            setIsPublic(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!file) {
            toast.error('Veuillez sélectionner un fichier');
            return;
        }
        if (!title.trim()) {
            toast.error('Veuillez saisir un titre');
            return;
        }

        try {
            setUploading(true);
            const formData = new FormData();
            formData.append('file', file);
            formData.append('title', title);
            formData.append('description', description);
            formData.append('category', category);
            formData.append('brand', brand);
            formData.append('is_public', String(isPublic));
            formData.append('visible_to_client', String(visibleToClient));
            formData.append('visible_to_apporteur', String(visibleToApporteur));
            formData.append('visible_to_transporteur', String(visibleToTransporteur));
            formData.append('visible_to_fournisseur', String(visibleToFournisseur));

            await api.post('/documents', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            toast.success('Document ajouté avec succès');
            onSuccess();
        } catch (error: any) {
            console.error('Error uploading document:', error);
            toast.error(error?.response?.data?.detail || 'Erreur lors de l\'upload');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-800 z-10">
                    <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                        Ajouter un document
                    </h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            Fichier *
                        </label>
                        <input
                            type="file"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                            accept=".pdf,.jpg,.jpeg,.png,.gif"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                            PDF et Images uniquement (max 50 MB)
                        </p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            Titre *
                        </label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Titre du document"
                            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            Description
                        </label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Description du document..."
                            rows={3}
                            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            Marque
                        </label>
                        <select
                            value={brand}
                            onChange={(e) => setBrand(e.target.value)}
                            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                        >
                            <option value="nova_flora">NOVA FLORA</option>
                            <option value="douce_france">DOUCE FRANCE</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            Catégorie
                        </label>
                        <select
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                        >
                            {sortedCategories.map((cat) => (
                                <option key={cat.value} value={cat.value}>
                                    {cat.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="is_public"
                            checked={isPublic}
                            onChange={(e) => handleIsPublicChange(e.target.checked)}
                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-slate-300 rounded"
                        />
                        <label htmlFor="is_public" className="text-sm text-slate-700 dark:text-slate-300">
                            Visible par tous les utilisateurs
                        </label>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            Visibilité par rôle
                        </label>
                        <div className="space-y-2 pl-2">
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="visible_client"
                                    checked={visibleToClient}
                                    onChange={(e) => handleRoleVisibilityChange('client', e.target.checked)}
                                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-slate-300 rounded"
                                />
                                <label htmlFor="visible_client" className="text-sm text-slate-700 dark:text-slate-300">
                                    Client
                                </label>
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="visible_apporteur"
                                    checked={visibleToApporteur}
                                    onChange={(e) => handleRoleVisibilityChange('apporteur', e.target.checked)}
                                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-slate-300 rounded"
                                />
                                <label htmlFor="visible_apporteur" className="text-sm text-slate-700 dark:text-slate-300">
                                    Apporteur
                                </label>
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="visible_transporteur"
                                    checked={visibleToTransporteur}
                                    onChange={(e) => handleRoleVisibilityChange('transporteur', e.target.checked)}
                                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-slate-300 rounded"
                                />
                                <label htmlFor="visible_transporteur" className="text-sm text-slate-700 dark:text-slate-300">
                                    Transporteur
                                </label>
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="visible_fournisseur"
                                    checked={visibleToFournisseur}
                                    onChange={(e) => handleRoleVisibilityChange('fournisseur', e.target.checked)}
                                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-slate-300 rounded"
                                />
                                <label htmlFor="visible_fournisseur" className="text-sm text-slate-700 dark:text-slate-300">
                                    Fournisseur
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            disabled={uploading || !file}
                            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {uploading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    Upload...
                                </>
                            ) : (
                                <>
                                    <Upload className="h-4 w-4" />
                                    Ajouter
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

interface EditModalProps {
    document: AppDocument;
    categories: Category[];
    onClose: () => void;
    onSuccess: () => void;
}

function EditModal({ document, categories, onClose, onSuccess }: EditModalProps) {
    const [title, setTitle] = useState(document.title);
    const [description, setDescription] = useState(document.description || '');
    const [category, setCategory] = useState(() => {
        const exists = categories.some(c => c.value === document.category);
        return exists ? document.category : (categories[0]?.value || '');
    });
    const [brand, setBrand] = useState(document.brand || 'nova_flora');
    const [isPublic, setIsPublic] = useState(document.is_public);
    const [visibleToClient, setVisibleToClient] = useState(document.visible_to_client ?? true);
    const [visibleToApporteur, setVisibleToApporteur] = useState(document.visible_to_apporteur ?? true);
    const [visibleToTransporteur, setVisibleToTransporteur] = useState(document.visible_to_transporteur ?? true);
    const [visibleToFournisseur, setVisibleToFournisseur] = useState(document.visible_to_fournisseur ?? true);
    const [updating, setUpdating] = useState(false);
    const sortedCategories: Category[] = [...categories].sort((first: Category, second: Category) => (
        first.label.localeCompare(second.label, 'fr', { sensitivity: 'base' })
    ));

    const handleIsPublicChange = (checked: boolean) => {
        setIsPublic(checked);
        if (checked) {
            setVisibleToClient(true);
            setVisibleToApporteur(true);
            setVisibleToTransporteur(true);
            setVisibleToFournisseur(true);
        }
    };

    const handleRoleVisibilityChange = (role: 'client' | 'apporteur' | 'transporteur' | 'fournisseur', checked: boolean) => {
        switch (role) {
            case 'client':
                setVisibleToClient(checked);
                break;
            case 'apporteur':
                setVisibleToApporteur(checked);
                break;
            case 'transporteur':
                setVisibleToTransporteur(checked);
                break;
            case 'fournisseur':
                setVisibleToFournisseur(checked);
                break;
        }

        if (!checked) {
            setIsPublic(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!title.trim()) {
            toast.error('Veuillez saisir un titre');
            return;
        }

        try {
            setUpdating(true);
            await api.patch(`/documents/${document.id}`, {
                title,
                description,
                category,
                brand,
                is_public: isPublic,
                visible_to_client: visibleToClient,
                visible_to_apporteur: visibleToApporteur,
                visible_to_transporteur: visibleToTransporteur,
                visible_to_fournisseur: visibleToFournisseur,
            });

            toast.success('Document mis à jour avec succès');
            onSuccess();
        } catch (error: any) {
            console.error('Error updating document:', error);
            toast.error(error?.response?.data?.detail || 'Erreur lors de la mise à jour');
        } finally {
            setUpdating(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-800">
                    <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                        Paramètres du document
                    </h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            Titre *
                        </label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Titre du document"
                            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            Description
                        </label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Description du document..."
                            rows={3}
                            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            Marque
                        </label>
                        <select
                            value={brand}
                            onChange={(e) => setBrand(e.target.value)}
                            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                        >
                            <option value="nova_flora">NOVA FLORA</option>
                            <option value="douce_france">DOUCE FRANCE</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            Catégorie
                        </label>
                        <select
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                        >
                            {sortedCategories.map((cat) => (
                                <option key={cat.value} value={cat.value}>
                                    {cat.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="edit_is_public"
                            checked={isPublic}
                            onChange={(e) => handleIsPublicChange(e.target.checked)}
                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-slate-300 rounded"
                        />
                        <label htmlFor="edit_is_public" className="text-sm text-slate-700 dark:text-slate-300">
                            Visible par tous les utilisateurs
                        </label>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            Visibilité par rôle
                        </label>
                        <div className="space-y-2 pl-2">
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="edit_visible_client"
                                    checked={visibleToClient}
                                    onChange={(e) => handleRoleVisibilityChange('client', e.target.checked)}
                                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-slate-300 rounded"
                                />
                                <label htmlFor="edit_visible_client" className="text-sm text-slate-700 dark:text-slate-300">
                                    Client
                                </label>
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="edit_visible_apporteur"
                                    checked={visibleToApporteur}
                                    onChange={(e) => handleRoleVisibilityChange('apporteur', e.target.checked)}
                                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-slate-300 rounded"
                                />
                                <label htmlFor="edit_visible_apporteur" className="text-sm text-slate-700 dark:text-slate-300">
                                    Apporteur
                                </label>
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="edit_visible_transporteur"
                                    checked={visibleToTransporteur}
                                    onChange={(e) => handleRoleVisibilityChange('transporteur', e.target.checked)}
                                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-slate-300 rounded"
                                />
                                <label htmlFor="edit_visible_transporteur" className="text-sm text-slate-700 dark:text-slate-300">
                                    Transporteur
                                </label>
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="edit_visible_fournisseur"
                                    checked={visibleToFournisseur}
                                    onChange={(e) => handleRoleVisibilityChange('fournisseur', e.target.checked)}
                                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-slate-300 rounded"
                                />
                                <label htmlFor="edit_visible_fournisseur" className="text-sm text-slate-700 dark:text-slate-300">
                                    Fournisseur
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            disabled={updating}
                            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {updating ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    Mise à jour...
                                </>
                            ) : (
                                <>
                                    <Settings className="h-4 w-4" />
                                    Enregistrer
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
